import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { emailExistsChecker, LogInWithEmail } from "../../../api/UserController";
import { SUPERADMIN_ROLE, ADMIN_ROLE, GUEST_ROLE, USER_ROLE } from "../../../constants";
import { SaveMerchant } from "../../../redux/actions/MerchantActions";
import { SaveUser, RefreshAccessToken } from "../../../redux/actions/UserActions";
import Button from "../../../components/common/button/Button";
import { isValidEmail } from "../../../utils/generalUtils";
import { CheckBoxMain } from "../../../components/common/checkBox/CheckBoxMain";
import { setColor, setFont } from "../../../assets/styles/styles";
import { Input } from "../../../components/common/input/Input";
import ResetPasswordPopUp from "./ResetPasswordPopUp";

const LoginForm = ({ setMessage }) => {
    const FIVE = 5;
    const dispatch = useDispatch();

    const [emailValue, setEmailValue] = useState("");
    const [emailErrorMsg, setEmailErrorMsg] = useState("");
    const [isEmailError, setIsEmailError] = useState(false);

    const [passwordValue, setPasswordValue] = useState("");
    const [passwordErrorMsg, setPasswordErrorMsg] = useState("");
    const [isPasswordError, setIsPasswordError] = useState(false);

    const [birthdayValue, setBirthdayValue] = useState("");
    const [isBirthdayInputValid, setIsBirthdayInputValid] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [shouldDisableAutoComplete, setShouldDisableAutoComplete] = useState(true);

    const [loginCounter, setLoginCounter] = useState(0);

    const [isPopupVisible, setIsPopupVisible] = useState(false);

    const [isBirthdayInputRequired, setIsBirthdayInputRequired] = useState(false);

    const password = useRef();

    useEffect(() => {
        const hasVisited = localStorage.getItem("hasVisitedLoginPage");
        if (hasVisited) {
            setShouldDisableAutoComplete(false);
        } else {
            localStorage.setItem("hasVisitedLoginPage", "true");
        }
    }, []);

    const handleEmailChange = async (e) => {
        const { value } = e.target;
        setEmailValue(value);

        if (isEmailError && value !== "") {
            setIsEmailError(false);
            setEmailErrorMsg("");
        }

        if (value !== "") {
            try {
                // second parameter true returns full response with userBirthday
                const res = await emailExistsChecker(value, true);
                if (res.exists) {
                    if (res.data.userBirthday === null || res.data.userBirthday === "") {
                        setIsBirthdayInputRequired(true);
                    } else {
                        setIsBirthdayInputRequired(false);
                    }
                } else {
                    setIsBirthdayInputRequired(false);
                }
            } catch (error) {
                setMessage("Error with server, please try again later");
                setIsBirthdayInputRequired(false);
            }
        }
    };

    const handlePasswordChange = (e) => {
        const { value } = e.target;
        setPasswordValue(value);

        if (isPasswordError && value !== "") {
            setIsPasswordError(false);
            setPasswordErrorMsg("");
        }
    };

    const handleFormValidtion = async () => {
        if (emailValue === "") {
            setIsEmailError(true);
            setEmailErrorMsg("Email is required");
            return false;
        }

        if (!isValidEmail(emailValue)) {
            setIsEmailError(true);
            setEmailErrorMsg("Email must be a valid email address");
            return false;
        }

        if (passwordValue === "") {
            setIsPasswordError(true);
            setPasswordErrorMsg("Password is required");
            return false;
        }

        if (isBirthdayInputRequired && birthdayValue === "") {
            setMessage("Birthday is required");
            return false;
        }

        return true;
    };

    const closePopup = () => {
        setIsPopupVisible(false);
    };

    const handleBirthdayInput = (e) => {
        let value = e.target.value.replace(/[^\d/]/g, "");

        if (value.includes("/")) {
            const parts = value.split("/");
            value = parts.join("");
        }

        if (value.length > 4) {
            value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
        } else if (value.length > 2) {
            value = `${value.slice(0, 2)}/${value.slice(2)}`;
        }

        value = value.slice(0, 10);

        setBirthdayValue(value);

        let valid = /^\d{2}\/\d{2}\/\d{4}$/.test(value);

        if (valid) {
            const [dd, mm, yyyy] = value.split("/").map(Number);
            const now = new Date();
            const maxYear = now.getFullYear();

            if (yyyy < 1900 || yyyy > maxYear) valid = false;
            if (mm < 1 || mm > 12) valid = false;
            if (dd < 1 || dd > 31) valid = false;

            const dateObj = new Date(yyyy, mm - 1, dd);
            if (dateObj.getFullYear() !== yyyy || dateObj.getMonth() !== mm - 1 || dateObj.getDate() !== dd) {
                valid = false;
            }

            if (dateObj > now) valid = false;
        }

        setIsBirthdayInputValid(valid);

        if (!valid) {
            setMessage("Birthday must be in the format DD/MM/YYYY and a valid date");
        } else {
            setMessage("");
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        setMessage("");
        setIsEmailError(false);
        setEmailErrorMsg("");
        setIsPasswordError(false);
        setPasswordErrorMsg("");

        if (!(await handleFormValidtion())) {
            setIsLoading(false);
            return;
        }

        try {
            let formattedBirthday = birthdayValue;
            if (isBirthdayInputRequired && isBirthdayInputValid) {
                // reverse the birthday value to YYYY-MM-DD format
                const [dd, mm, yyyy] = formattedBirthday.split("/").map(Number);
                formattedBirthday = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
            }

            const res = await LogInWithEmail(emailValue, passwordValue, formattedBirthday, "");

            if (res.status === 200) {
                const role = res.userRoles[0];

                // leaving code in case user tries to login without birthday
                if ((res.user.userBirthday === null || res.user.userBirthday === "") && birthdayValue === "") {
                    setMessage("Please update your profile with your birthday");
                    setIsBirthdayInputRequired(true);
                    return;
                }

                if (role === SUPERADMIN_ROLE || role === ADMIN_ROLE || res.isMerchant) {
                    setMessage("Login Successful. Admin");

                    if (res.isMerchant) {
                        const {
                            merchantName,
                            merchantId,
                            street,
                            city,
                            country,
                            postalCode,
                            merchantBusinessType,
                            stripeClientId,
                        } = res.merchant;
                        const address = { street, city, country, postalCode };

                        dispatch(SaveMerchant(merchantName, merchantId, address, merchantBusinessType, stripeClientId));
                    }

                    if (res.userRoles[0] === SUPERADMIN_ROLE || res.userRoles[0] === ADMIN_ROLE || res.isMerchant) {
                        dispatch(
                            SaveUser(
                                res.user.email,
                                res.user.displayName,
                                res.token,
                                res.refreshToken,
                                res.userId,
                                res.userRoles[0],
                                res.isMerchant,
                                res.expiration,
                                res.profileIconBase64,
                                res.user.userBirthday,
                                res.isUserOwnerOfClient
                            )
                        );
                    }
                } else if (res.userRoles[0] === GUEST_ROLE || res.userRoles[0] === USER_ROLE) {
                    dispatch(
                        SaveUser(
                            res.user.email,
                            res.user.displayName,
                            res.token,
                            res.refreshToken,
                            res.userId,
                            res.userRoles[0],
                            res.isMerchant,
                            res.expiration,
                            res.profileIconBase64,
                            res.user.userBirthday,
                            res.isUserOwnerOfClient
                        )
                    );
                } else {
                    dispatch(
                        SaveUser(
                            res.user.email,
                            res.user.displayName,
                            res.token,
                            res.refreshToken,
                            res.userId,
                            "",
                            res.isMerchant,
                            res.expiration,
                            res.profileIconBase64,
                            res.user.userBirthday,
                            res.isUserOwnerOfClient
                        )
                    );
                }
            } else {
                setLoginCounter(loginCounter + 1);
                if (loginCounter + 1 >= FIVE) {
                    setIsPopupVisible(true);
                }
                setMessage("Failed to login, please ensure your information is correct");
            }
        } catch (error) {
            console.error("API call error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StyledForm
            id="formLogin"
            onSubmit={(e) => onSubmit(e)}
            autoComplete={shouldDisableAutoComplete ? "off" : "on"}
        >
            {isPopupVisible && (
                <Overlay>
                    <ResetPasswordPopUp onClose={closePopup} />
                </Overlay>
            )}
            <input
                type="email"
                style={{ display: "none" }}
                autoComplete={shouldDisableAutoComplete ? "off" : "email"}
            />
            <input
                type="password"
                style={{ display: "none" }}
                autoComplete={shouldDisableAutoComplete ? "off" : "new-password"}
            />
            <Input
                variant="LoginInput"
                type="email"
                placeholder="Email address"
                value={emailValue}
                onChangeHandler={handleEmailChange}
            />
            {isEmailError && !isValidEmail(emailValue) && <ErrorMessage>{emailErrorMsg}</ErrorMessage>}

            <Input
                variant="LoginInput"
                type="password"
                placeholder="Password"
                ref={password}
                value={passwordValue}
                onChangeHandler={handlePasswordChange}
            />
            {isPasswordError && <ErrorMessage>{passwordErrorMsg}</ErrorMessage>}

            {isBirthdayInputRequired && (
                <Input
                    variant="LoginInput"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={birthdayValue || ""}
                    onChangeHandler={handleBirthdayInput}
                />
            )}

            <StyledCheckboxLabel>
                <CheckBoxMain
                    height={"25px"}
                    width={"25px"}
                    fontSize={16}
                    labelTextRight={"Stay signed in"}
                    onChange={() => {}}
                />
            </StyledCheckboxLabel>
            <Button
                label="SIGN IN"
                buttonWidth={"100%"}
                variant={
                    !isValidEmail(emailValue) ||
                    passwordValue === "" ||
                    (isBirthdayInputRequired && birthdayValue === "")
                        ? "DefaultButton"
                        : "PinkButton"
                }
                type="submit"
                disabled={isLoading || (isBirthdayInputRequired && !isBirthdayInputValid)}
            >
                SIGN IN
            </Button>

            <StyledLink to="/reset-password">Forgot password?</StyledLink>
        </StyledForm>
    );
};

LoginForm.propTypes = {
    setMessage: PropTypes.func.isRequired,
};

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 600px;
    padding: 20px 20px 0px 20px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 90%;
    }

    @media (max-width: 480px) {
        width: 100%;
    }
`;

const ErrorMessage = styled.div`
    line-height: 1.5;
    font-size: 0.75rem;
    font-family: ${setFont.barlow};
    font-weight: 400;
    text-align: left;
    color: ${setColor.errorColor};
    margin-left: 10px;
`;

const StyledCheckboxLabel = styled.div`
    width: fit-content;
    display: flex;
    padding: 5px 10px 30px 1px;
    margin-top: 10px;
`;

const StyledLink = styled(RouterLink)`
    color: ${setColor.accent4};
    text-decoration: none;
    text-align: center;
    font-size: 18px;
    color: ${setColor.purpleNavigation};
    text-transform: uppercase;
    font-weight: 700;
    display: flex;
    justify-content: center;
    margin: 20px;
    :hover {
        color: ${setColor.accent4};
    }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
`;

export default LoginForm;
